<template>
    <div class="tw:mt-2 tw:mb-4">
      <Dialog :open="showNewFieldModal" @update:open="onDialogUpdate">
        <DialogTrigger
          class="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-dashed tw:px-3 tw:py-2 tw:text-sm tw:text-primary hover:tw:bg-muted"
        >
          <Icon name="CirclePlus" />
          {{ $t('Add new field') }}
        </DialogTrigger>
        <DialogScrollContent>
          <DialogHeader>
            <DialogTitle>
              {{ $t('Select fields to add') }}
            </DialogTitle>
          </DialogHeader>
          <ul class="tw:my-2 tw:flex tw:flex-col tw:divide-y tw:rounded-md tw:border">
            <li v-for="(field, i) in selectableFields" :key="field.name">
              <label
                :for="'frmField' + i"
                class="tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:px-3 tw:py-2 tw:text-sm hover:tw:bg-muted"
              >
                <input
                  :id="'frmField' + i"
                  type="checkbox"
                  v-model="selectedFields"
                  :value="field.name"
                >
                {{ field.label }}
              </label>
            </li>
          </ul>
          <DialogFooter>
            <Button
              type="button"
              :disabled="selectedFields.length === 0"
              @click="onSubmitAddField"
            >
              {{ $t('Add field', selectedFields.length) }}
            </Button>
          </DialogFooter>
        </DialogScrollContent>
      </Dialog>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { AvailableField, FieldConfig, VisibilityConfig } from "@/types/FieldConfig";
import { Dialog, DialogScrollContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from '@/shadcn/components/ui/button';
import { Icon } from '@/components/ui/icon';

const props = defineProps<{
  availableFields: AvailableField[];
  currentFields: FieldConfig[];
}>();

const selectedFields = ref<string[]>([]);

const showNewFieldModal = ref(false);

const selectableFields = computed(() => {
  return props.availableFields.filter(field => {
    return !props.currentFields.find(currentField => currentField.name === field.name)
  })
});

const emit = defineEmits(['add-fields']);

/**
 * Closing has to clear the selection wherever it is triggered from. Setting the
 * ref does not make the dialog emit update:open, so a submit that only closed the
 * dialog left the selection behind and added it again next time.
 */
const closeDialog = () => {
  showNewFieldModal.value = false
  selectedFields.value = []
}

const onDialogUpdate = (isOpen: boolean) => {
  if (isOpen) {
    showNewFieldModal.value = true

    return
  }

  closeDialog()
}

const onSubmitAddField = () => {
    emit('add-fields',
      selectedFields.value.map((name) => {
        const field = props.availableFields.find(field => field.name === name)

        return <FieldConfig>{
          name: name,
          backoffice: <VisibilityConfig> {
            display: field.default_visibility?.backoffice?.display ?? false,
            required: field.default_visibility?.backoffice?.required ?? false
          },
          public: <VisibilityConfig> {
            display: field.default_visibility?.public?.display ?? false,
            required: field.default_visibility?.public?.required ?? false
          },
          mobileApp: <VisibilityConfig> {
            display: field.default_visibility?.mobileApp?.display ?? false,
            required: field.default_visibility?.mobileApp?.required ?? false
          },
          required: field.default_visibility?.required ?? false,
        }
      })
    )

    closeDialog()
  }
</script>

<style scoped>

</style>
